import {
  createMemoryHistory,
  createRouter,
  createWebHashHistory,
  createWebHistory,
} from 'vue-router';
import { route } from 'quasar/wrappers';
import { AxiosError } from 'axios';

import { loadClientFonts } from 'src/helpers/editor/customerFonts';
import getToken from 'src/helpers/editor/getToken';
import { installGTM, pushDataLayer } from 'src/helpers/gtm';
import { useLoaderStore } from 'src/stores/loader';
import { type AccessTokenJWT, useLoginStore } from 'src/stores/login';
import { useSettingsStore } from 'src/stores/settings';

import routes from './routes';

/*
 * Se não estiver construindo no modo SSR, você pode
 * exportar diretamente a instancia do Router;
 *
 * A função abaixo também pode ser assíncrona.
 * Use async/await ou retorne uma Promise que
 * resolva com a instância do Router.
 */
export default route(function (/* { store, ssrContext } */) {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === 'history'
      ? createWebHistory
      : createWebHashHistory;

  const Router = createRouter({
    /** Ref: https://router.vuejs.org/guide/advanced/scroll-behavior.html */
    scrollBehavior(to, from, savedPosition) {
      /**
       * O timeout é necessário para aguardar a transição entre páginas (ex. no MainLayout),
       * antes de zerar o scroll. É importante manter o mesmo tempo nos dois lugares.
       */
      return new Promise((resolve) => {
        setTimeout(() => {
          if (to.hash) {
            resolve({
              el: to.hash,
              behavior: 'smooth',
            });
          } else if (savedPosition) {
            resolve(savedPosition);
          } else {
            resolve({ left: 0, top: 0 });
          }
        }, 200);
      });
    },
    routes,
    // Deixe isso como está e faça as alterações no quasar.config.ts!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(process.env.VUE_ROUTER_BASE),
  });

  Router.beforeEach(async (to, from) => {
    const loginStore = useLoginStore();

    //
    // Verificação de manutenção — redireciona imediatamente se detectar
    //
    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
      });

      if (response.ok) {
        const data = await response.json();
        const serverVersion = data.version as string;

        if (serverVersion.includes('maintenance')) {
          if (to.path !== '/maintenance') {
            localStorage.removeItem('editor-token');
            loginStore.$reset();
            window.location.href = '/maintenance';
            return false;
          }

          return; // Na rota de manutenção, não executa a lógica de autenticação
        } else {
          if (to.path === '/maintenance') {
            window.location.replace('/login');
            return false;
          }
        }
      }
    } catch {
      // Falha silenciosa — não impede a navegação
    }

    const loader = useLoaderStore();
    loader.loading = true;

    if (to.name === 'error') {
      return; // Abortar execução na página de erro.
    }

    //
    // Declaração e validação do token
    //
    const userToken = getToken({ decoded: true }) as AccessTokenJWT | null;
    const hasToken = userToken !== null;

    //
    // Acesso ao Login
    //
    const isLoginPage = to.name === 'login';
    const { flow } = to.query; // Fluxo de redefinição/criação de senha

    if (isLoginPage) {
      if (hasToken && !flow) {
        window.location.href = '/'; // Forçar atualização para carregar o GTM.
      }

      return;
    }

    //
    // Usuário sem acesso
    //
    if (!hasToken) {
      Router.push({ path: '/login' });

      return;
    }

    //
    // Usuário com acesso expirado
    //
    const freshLoad = from.matched.length === 0;
    const isTokenExpired = (userToken.exp as number) < Date.now() / 1000;

    if (isTokenExpired) {
      localStorage.removeItem('editor-token');
      loginStore.$reset();

      if (freshLoad) {
        Router.push({ path: '/login' });
      } else {
        // eslint-disable-next-line no-alert
        alert('Seu acesso expirou, realize o login novamente.');
        window.location.href = '/login'; // Forçar atualização para carregar o GTM.
      }

      return;
    }

    //
    // Criação do LoggedUser
    //
    loginStore.loggedUser = {
      email: userToken.email,
      global: Boolean(userToken.global),
      role: userToken.role,
      origin: userToken.sub,
      name: userToken.name,
    };

    //
    // Acesso à área Letsbooker
    //
    const isLetsbookerUser = loginStore.isLetsbooker;
    const isLetsbookerPage = to.name === 'letsbookers';

    if (isLetsbookerUser && !userToken.aud) {
      if (!isLetsbookerPage) {
        Router.push({ path: '/letsbookers' });
      }

      return;
    }

    if (!isLetsbookerUser && isLetsbookerPage) {
      Router.push({ path: '/' });

      return;
    }

    //
    // Acesso à área de edição (canvas)
    //
    const isCanvasUrl = to.name === 'editor';
    const { isViewerUser } = loginStore;

    if (isViewerUser && isCanvasUrl) {
      // eslint-disable-next-line no-alert
      alert('Você não possui permissão para acessar esta área.');
      Router.push({ path: '/' });

      return;
    }

    //
    // Carregamento das configurações do cliente
    //
    const settingsStore = useSettingsStore();

    settingsStore.customer = userToken.aud as string;

    // Carrega fontes exclusivas do cliente de forma condicional
    loadClientFonts(settingsStore.customer);

    //
    // GTM interno
    // Não rodar no preview, se já tiver instalado ou localmente
    //
    if (
      to.name !== 'preview' &&
      !('google_tag_manager' in window) &&
      window.location.hostname !== 'localhost'
    ) {
      pushDataLayer({ customer: userToken.aud });
      installGTM();
    }

    //
    // Carregar configurações na atualização completa.
    //
    if (freshLoad) {
      try {
        await settingsStore.loadCustomerSettings();
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 404) {
          console.warn('Cliente não existe');

          if (userToken.sub === 'crm') {
            // cria customer com base apenas no nome no token
            await settingsStore.saveCustomerSettings({});
          }
        }
      }
    }
  });

  Router.afterEach(() => {
    const loader = useLoaderStore();
    loader.loading = false;
  });

  return Router;
});
